import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Seo from "@/lib/seo";
import { api, API } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import StatusPill, { STATUS_LABELS } from "@/components/StatusPill";
import AdminReports from "@/components/AdminReports";
import AdminPayouts from "@/components/AdminPayouts";
import AdminDistribution, { DeliveryPill } from "@/components/AdminDistribution";
import ReleaseCalendar from "@/components/ReleaseCalendar";
import { useSearchParams } from "react-router-dom";
import { Star, PaperPlaneTilt, Trash } from "@phosphor-icons/react";

const FILTERS = ["all", "in_review", "approved", "live", "rejected"];

export default function Admin() {
  const [releases, setReleases] = useState([]);
  const [allReleases, setAllReleases] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState("in_review");
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "review");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [writers, setWriters] = useState("");
  const [artistName, setArtistName] = useState("");
  const saveWriters = async () => {
    try {
      const { data } = await api.patch(`/admin/releases/${selected.id}/writers`, { writers, artist_name: artistName || null });
      setWriters(data.writers); toast.success("Credits saved"); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const [busy, setBusy] = useState(false);
  const [tlConnected, setTlConnected] = useState(false);

  const load = useCallback(() => {
    const params = filter === "all" ? {} : { status: filter };
    api.get("/admin/releases", { params }).then(({ data }) => setReleases(data.releases));
    api.get("/admin/releases").then(({ data }) => setAllReleases(data.releases));
    api.get("/admin/stats").then(({ data }) => setStats(data));
    api.get("/toolost/status").then(({ data }) => setTlConnected(data.connected)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const deliver = async (r) => {
    try {
      await api.post(`/admin/releases/${r.id}/deliver`);
      toast.success("Delivery to Too Lost started");
      setTimeout(load, 1500);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not deliver"); }
  };

  const toggleFeature = async (r) => {
    try {
      await api.patch(`/admin/releases/${r.id}/feature`, { featured: !r.featured });
      toast.success(!r.featured ? "Added to landing page spotlight" : "Removed from spotlight");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const deleteRelease = async (r) => {
    if (!window.confirm(`Permanently delete "${r.title}" (${r.release_date}) by ${r.artist_name}?\n\nThis removes it from the site, calendar, spotlight and pre-save page. It does NOT take it down from stores.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/releases/${r.id}`);
      toast.success(`Deleted "${r.title}" (${r.release_date})`);
      setSelected(null); load();
    } catch (e) {
      const msg = e.response?.data?.detail || "Delete failed";
      if (e.response?.status === 409 && window.confirm(`${msg}\n\nDelete from Asylum anyway?`)) {
        try { await api.delete(`/admin/releases/${r.id}?force=true`); toast.success(`Deleted "${r.title}"`); setSelected(null); load(); }
        catch (e2) { toast.error(e2.response?.data?.detail || "Delete failed"); }
      } else toast.error(msg);
    } finally { setBusy(false); }
  };

  const updateStatus = async (release_id, status) => {
    setBusy(true);
    try {
      await api.patch(`/admin/releases/${release_id}`, { status, admin_notes: notes || null });
      toast.success(`Marked as ${STATUS_LABELS[status]}`);
      setSelected(null); setNotes(""); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Seo title="Admin" path="/admin" noindex description="Admin." />
      <Navbar />
      <div className="max-w-[1600px] mx-auto px-6 lg:px-10 py-12">
        <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-3">— Admin</div>
        <h1 className="font-display text-4xl lg:text-6xl uppercase tracking-tighter mb-10" data-testid="admin-heading">
          A&R Review<span className="text-red-600">.</span>
        </h1>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-0 border border-zinc-800 mb-10" data-testid="admin-stats">
            <StatBox n={stats.total_users} l="Artists" />
            <StatBox n={stats.active_subscribers} l="Active Subs" />
            <StatBox n={stats.total_releases} l="Total Releases" />
            <StatBox n={stats.in_review} l="In Review" accent />
            <StatBox n={stats.live} l="Live" />
          </div>
        )}

        <div className="flex gap-0 border border-zinc-800 w-fit mb-8" data-testid="admin-tabs">
          {[["review", "A&R Review"], ["calendar", "Calendar"], ["distribution", "Distribution"], ["reports", "Streaming Reports"], ["payouts", "Payouts"]].map(([id, l]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`px-6 py-3 text-xs tracking-[0.2em] uppercase border-r border-zinc-800 last:border-r-0 transition-colors ${tab === id ? "bg-white text-black" : "text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>

        {tab === "payouts" ? <AdminPayouts /> : tab === "reports" ? <AdminReports releases={allReleases} /> : tab === "distribution" ? <AdminDistribution /> : tab === "calendar" ? <ReleaseCalendar title="Roster Release Calendar" /> : (<>
        <div className="flex flex-wrap gap-2 mb-6">
          {FILTERS.map(f => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`admin-filter-${f}`}
              className={`text-xs tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${filter === f ? "border-red-600 bg-red-600 text-white" : "border-zinc-800 text-zinc-400 hover:border-zinc-600"}`}>
              {f === "all" ? "All" : STATUS_LABELS[f]}
            </button>
          ))}
        </div>

        <div className="border border-zinc-800 overflow-x-auto" data-testid="admin-releases-table">
          <div className="min-w-[900px]">
            <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-zinc-800 text-[10px] tracking-[0.2em] uppercase text-zinc-500 bg-zinc-950">
              <div className="col-span-3">Title</div>
              <div className="col-span-2">Artist</div>
              <div className="col-span-3">Submitted By</div>
              <div className="col-span-1">Tier</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {releases.length === 0 ? (
              <div className="p-12 text-center text-zinc-500 text-sm">No releases in this bucket.</div>
            ) : releases.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-zinc-800 last:border-b-0 row-hover items-center text-sm" data-testid={`admin-release-${r.id}`}>
                <div className="col-span-3 truncate">
                  <div className="font-medium">{r.title}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">{r.release_type || "single"} · {r.tracks?.length || 1} trk · {r.genre} • {r.release_date}</div>
                </div>
                <div className="col-span-2 text-zinc-400 truncate">{r.artist_name}</div>
                <div className="col-span-3 truncate">
                  <div>{r.user_display_name}</div>
                  <div className="text-[10px] text-zinc-500">{r.user_email}</div>
                </div>
                <div className="col-span-1 text-[10px] tracking-widest uppercase text-zinc-400">{r.user_tier?.replace("asylum_", "") || "—"}</div>
                <div className="col-span-1"><StatusPill status={r.status} /><div className="mt-1"><DeliveryPill delivery={r.delivery} /></div></div>
                <div className="col-span-2 text-right flex justify-end gap-2">
                  {(r.status === "approved" || r.status === "live") && !r.delivery?.toolost_release_id && r.delivery?.status !== "delivering" && (
                    <button onClick={() => deliver(r)} data-testid={`admin-deliver-${r.id}`} title={tlConnected ? "Deliver to Too Lost" : "Connect Too Lost first (Distribution tab)"}
                      className={`text-xs border px-2 py-1.5 transition-colors ${tlConnected ? "border-zinc-700 text-zinc-400 hover:border-green-500 hover:text-green-500" : "border-zinc-800 text-zinc-700"}`}>
                      <PaperPlaneTilt size={12} />
                    </button>
                  )}
                  {(r.status === "approved" || r.status === "live") && (
                    <button onClick={() => toggleFeature(r)} data-testid={`admin-feature-${r.id}`} title={r.featured ? "Remove from spotlight" : "Feature on landing page"}
                      className={`text-xs border px-2 py-1.5 transition-colors ${r.featured ? "border-yellow-500 text-yellow-500" : "border-zinc-700 text-zinc-500 hover:border-yellow-500 hover:text-yellow-500"}`}>
                      <Star size={12} weight={r.featured ? "fill" : "regular"} />
                    </button>
                  )}
                  <button onClick={() => { setSelected(r); setNotes(r.admin_notes || ""); setWriters(r.writers || r.artist_name || ""); setArtistName(r.artist_name || ""); }} data-testid={`admin-review-${r.id}`}
                    className="text-xs tracking-[0.15em] uppercase border border-zinc-700 px-3 py-1.5 hover:border-red-600 hover:text-red-500 transition-colors">
                    Review
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
        </>)}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setSelected(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl bg-black border border-zinc-800 p-6 sm:p-8 my-4 max-h-[calc(100vh-2rem)] overflow-y-auto" data-testid="admin-review-modal">
            <div className="text-[10px] tracking-[0.3em] uppercase text-red-500 mb-2">— Review</div>
            <div className="font-display text-3xl uppercase tracking-tighter mb-1">{selected.title}</div>
            <div className="text-sm text-zinc-500 mb-6">by {selected.artist_name} • {selected.genre}</div>

            <div className="grid grid-cols-2 gap-4 mb-6 text-sm">
              <Info label="Release Date" v={selected.release_date} />
              <Info label="Explicit" v={selected.explicit ? "Yes" : "No"} />
              <Info label="ISRC" v={selected.isrc || "—"} />
              <Info label="Status" v={STATUS_LABELS[selected.status]} />
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Tracks ({selected.tracks?.length || 1})</div>
            <div className="border border-zinc-800 divide-y divide-zinc-800 mb-6 max-h-40 overflow-y-auto" data-testid="admin-tracklist">
              {(selected.tracks?.length ? selected.tracks : [{ track_number: 1, title: selected.title, audio_path: selected.audio_path }]).map(t => (
                <div key={t.track_number} className="flex items-center gap-3 px-3 py-2 text-sm">
                  <span className="font-display text-zinc-600 w-6">{String(t.track_number).padStart(2, "0")}</span>
                  <span className="flex-1 truncate">{t.title}</span>
                  <audio controls preload="none" className="h-7 w-48" src={`${API}/files/${t.audio_path}?auth=${localStorage.getItem("asylum_token")}`} />
                </div>
              ))}
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Platforms</div>
            <div className="flex flex-wrap gap-1 mb-6">
              {selected.platforms.map(p => <span key={p} className="text-[10px] tracking-widest uppercase border border-zinc-800 px-2 py-1">{p}</span>)}
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Display Artist Name <span className="text-zinc-600">(what stores show)</span></div>
            <input value={artistName} onChange={(e) => setArtistName(e.target.value)} data-testid="admin-artist-name"
              className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none mb-4" />
            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Songwriters <span className="text-zinc-600">(full legal names, comma-separated — required by stores; shown only in "Written by" credits)</span></div>
            <div className="flex gap-2 mb-6">
              <input value={writers} onChange={(e) => setWriters(e.target.value)} data-testid="admin-writers" placeholder="e.g. Whitney Moses, Jane Doe"
                className="flex-1 bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none" />
              <button onClick={saveWriters} data-testid="admin-save-writers" className="border border-zinc-700 px-3 text-[10px] tracking-[0.2em] uppercase hover:border-white">Save</button>
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-2">Admin Notes</div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="admin-notes"
              className="w-full bg-transparent border border-zinc-800 px-3 py-2 text-sm focus:border-red-600 focus:outline-none mb-6" placeholder="Reason for rejection, notes for the artist..." />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <button disabled={busy} onClick={() => updateStatus(selected.id, "approved")} data-testid="admin-approve"
                className="border border-blue-500 text-blue-500 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-blue-500 hover:text-white transition-colors">Approve</button>
              <button disabled={busy} onClick={() => updateStatus(selected.id, "live")} data-testid="admin-live"
                className="border border-green-500 text-green-500 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-green-500 hover:text-white transition-colors">Push Live</button>
              <button disabled={busy} onClick={() => updateStatus(selected.id, "rejected")} data-testid="admin-reject"
                className="border border-red-600 text-red-600 py-3 text-[10px] tracking-[0.2em] uppercase hover:bg-red-600 hover:text-white transition-colors">Reject</button>
              <button disabled={busy} onClick={() => setSelected(null)} data-testid="admin-close"
                className="border border-zinc-700 text-zinc-400 py-3 text-[10px] tracking-[0.2em] uppercase hover:border-white hover:text-white transition-colors">Close</button>
            </div>
            <button disabled={busy} onClick={() => deleteRelease(selected)} data-testid="admin-delete-release"
              className="mt-3 w-full border border-zinc-800 text-zinc-500 py-2 text-[10px] tracking-[0.2em] uppercase hover:border-red-600 hover:text-red-500 transition-colors inline-flex items-center justify-center gap-2"><Trash size={12} /> Delete release permanently (duplicates, mistakes)</button>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
}

function StatBox({ n, l, accent }) {
  return (
    <div className={`p-6 border-r border-zinc-800 last:border-r-0 ${accent ? "bg-red-600/5" : ""}`}>
      <div className={`font-display text-4xl tracking-tighter ${accent ? "text-red-500" : ""}`}>{n}</div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mt-2">{l}</div>
    </div>
  );
}
function Info({ label, v }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 mb-1">{label}</div>
      <div>{v}</div>
    </div>
  );
}
